import type { FC } from 'react';
import { memo, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import { commonMessages } from '../../i18n/common';
import { Popover } from '../GraphModal/Popover';
import type { PopoverPropTypes, TippyInstance } from '../GraphModal/Popover';

type ConfirmationPopoverProps = PopoverPropTypes & {
  messageContent?: string;
  contentClassName?: string;
  wrapperClassName?: string;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
};

export const ConfirmationPopover: FC<ConfirmationPopoverProps> = memo(function ConfirmationPopover({
  messageContent,
  contentClassName,
  wrapperClassName,
  onConfirm,
  onCancel,
  ...tippyProps
}) {
  const [tippyInstance, setTippyInstance] = useState<TippyInstance>();
  const { className: tippyClassName, children, ...otherTippyProps } = tippyProps;

  const confirmationMessage = useMemo(
    () => messageContent || <FormattedMessage {...commonMessages.areYouSure} />,
    [messageContent],
  );

  const content = useMemo(
    () => (
      <div className="text-sm">
        <div className={twMerge('modal-body mb-6', contentClassName)}>{confirmationMessage}</div>
        <div className="flex justify-end gap-6">
          <button
            type="button"
            className="text-sm text-gray-500"
            onClick={async () => {
              await onCancel?.();
              tippyInstance?.hide();
            }}
          >
            <FormattedMessage {...commonMessages.no} />
          </button>
          <button
            type="button"
            className="px-4 py-1 text-sm text-white bg-primary rounded"
            onClick={async () => {
              await onConfirm?.();
              tippyInstance?.hide();
            }}
          >
            <FormattedMessage {...commonMessages.yes} />
          </button>
        </div>
      </div>
    ),
    [confirmationMessage, contentClassName, onCancel, onConfirm, tippyInstance],
  );

  return (
    <div className={twMerge('ConfirmationPopover_wrapper leading-none', wrapperClassName)}>
      <Popover
        content={content}
        onCreate={setTippyInstance}
        theme="light-border"
        trigger="click"
        className={twMerge('rounded-lg p-2', tippyClassName)}
        {...otherTippyProps}
      >
        {children}
      </Popover>
    </div>
  );
});
